import { useState, useEffect, useCallback } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Users,
  TrendingUp,
  Crown,
  BarChart3,
  Search,
  ChevronLeft,
  ChevronRight,
  X,
  Check,
  Minus,
  RefreshCw,
  LogOut,
  LogIn,
  Activity,
  CreditCard,
  Copy,
  ExternalLink,
  AlertTriangle,
  ShieldOff,
  ShieldCheck,
  Calendar,
  RotateCcw,
  Settings2,
  Clock,
  XCircle,
  Trash2,
} from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from 'recharts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TIER_LABELS = {
  founder_monthly: 'Founder Monthly',
  pastor_pro: 'Pastor Pro',
  founder_lifetime: 'Founder Lifetime',
  seminary_annual: 'Seminary Annual',
  ministry_plus: 'Ministry Plus (legacy)',
  church_team: 'Church Team (legacy)',
  pro: 'Pro (legacy)',
  trial_pending: 'Trial Pending (no card yet)',
  free: 'Free',
};

const TIER_COLORS = {
  founder_monthly: 'bg-amber-100 text-amber-800',
  pastor_pro: 'bg-blue-100 text-blue-800',
  founder_lifetime: 'bg-purple-100 text-purple-800',
  seminary_annual: 'bg-green-100 text-green-800',
  ministry_plus: 'bg-stone-100 text-stone-700',
  church_team: 'bg-stone-100 text-stone-700',
  pro: 'bg-stone-100 text-stone-700',
  trial_pending: 'bg-orange-100 text-orange-700',
  free: 'bg-gray-100 text-gray-600',
};

const PAID_TIERS = [
  'founder_monthly', 'pastor_pro', 'founder_lifetime', 'seminary_annual',
  'ministry_plus', 'church_team', 'pro',
];

function formatDate(dateStr) {
  if (!dateStr) return '—';
  try {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric',
    });
  } catch {
    return dateStr;
  }
}

function formatDateShort(dateStr) {
  if (!dateStr) return '';
  try {
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  } catch {
    return dateStr;
  }
}

function formatDateTime(dateStr) {
  if (!dateStr) return '—';
  try {
    return new Date(dateStr).toLocaleString('en-US', {
      month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
    });
  } catch {
    return dateStr;
  }
}

/** Fill in missing days in a by-day series so charts have a continuous x-axis */
function fillDays(data, days = 30) {
  const map = {};
  for (const d of data) map[d.date] = d.count;
  const result = [];
  for (let i = days - 1; i >= 0; i--) {
    const d = new Date();
    d.setUTCDate(d.getUTCDate() - i);
    const key = d.toISOString().slice(0, 10);
    result.push({ date: key, count: map[key] || 0 });
  }
  return result;
}

function StatCard({ icon: Icon, label, value, sub, color = 'text-[#1e3a8a]' }) {
  return (
    <div className="bg-white rounded-xl border border-stone-200 p-5 flex items-start gap-4">
      <div className={`p-2 rounded-lg bg-stone-50 ${color}`}>
        <Icon className="w-5 h-5" />
      </div>
      <div>
        <p className="text-sm text-stone-500 font-medium">{label}</p>
        <p className={`text-2xl font-bold font-['Libre_Baskerville'] ${color}`}>{value}</p>
        {sub && <p className="text-xs text-stone-400 mt-0.5">{sub}</p>}
      </div>
    </div>
  );
}

function FeatureIcon({ value }) {
  if (value === true) return <Check className="w-4 h-4 text-green-600" />;
  if (value === false) return <X className="w-4 h-4 text-red-400" />;
  return <Minus className="w-4 h-4 text-stone-300" />;
}

function MiniChart({ data, color, label }) {
  const filled = fillDays(data);
  const CustomTooltip = ({ active, payload, label: lbl }) => {
    if (!active || !payload?.length) return null;
    return (
      <div className="bg-white border border-stone-200 rounded-lg px-3 py-2 text-xs shadow-md">
        <p className="text-stone-500">{formatDateShort(lbl)}</p>
        <p className="font-semibold text-[#1f2937]">{payload[0].value} {label.toLowerCase()}</p>
      </div>
    );
  };
  return (
    <ResponsiveContainer width="100%" height={120}>
      <BarChart data={filled} margin={{ top: 4, right: 4, left: -28, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#f5f5f4" vertical={false} />
        <XAxis
          dataKey="date"
          tickFormatter={formatDateShort}
          tick={{ fontSize: 10, fill: '#a8a29e' }}
          axisLine={false}
          tickLine={false}
          interval={6}
        />
        <YAxis
          allowDecimals={false}
          tick={{ fontSize: 10, fill: '#a8a29e' }}
          axisLine={false}
          tickLine={false}
        />
        <Tooltip content={<CustomTooltip />} cursor={{ fill: '#f5f5f4' }} />
        <Bar dataKey="count" fill={color} radius={[3, 3, 0, 0]} />
      </BarChart>
    </ResponsiveContainer>
  );
}

// ── Copy-to-clipboard helper ──────────────────────────────────────
function CopyButton({ text }) {
  const [copied, setCopied] = useState(false);
  function handleCopy() {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  }
  return (
    <button onClick={handleCopy} className="ml-1 text-stone-400 hover:text-[#1e3a8a] transition-colors" title="Copy">
      {copied ? <Check className="w-3.5 h-3.5 text-green-600" /> : <Copy className="w-3.5 h-3.5" />}
    </button>
  );
}

// ── Section header ─────────────────────────────────────────────────
function SectionHeader({ children }) {
  return (
    <p className="text-xs font-semibold text-stone-400 uppercase tracking-wider mb-3">{children}</p>
  );
}

// ── Info row ───────────────────────────────────────────────────────
function InfoRow({ label, children }) {
  return (
    <div className="flex items-start justify-between gap-2 text-sm py-1.5 border-b border-stone-50 last:border-0">
      <span className="text-stone-500 flex-shrink-0">{label}</span>
      <span className="text-[#1f2937] text-right">{children}</span>
    </div>
  );
}

// ── Detail Panel ──────────────────────────────────────────────────
const ALL_TIERS = [
  'free', 'founder_monthly', 'pastor_pro', 'pastor_pro_monthly', 'pastor_pro_annual',
  'founder_lifetime', 'seminary_annual', 'ministry_plus', 'church_team', 'pro',
];

function UserDetailPanel({ userId, token, onClose, onUserUpdated }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Controls state
  const [extensionDays, setExtensionDays] = useState('30');
  const [extensionReason, setExtensionReason] = useState('');
  const [overrideTier, setOverrideTier] = useState('');
  const [overrideReason, setOverrideReason] = useState('');
  const [suspendReason, setSuspendReason] = useState('');
  const [resetReason, setResetReason] = useState('');
  const [cancelReason, setCancelReason] = useState('');
  const [deleteReason, setDeleteReason] = useState('');
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [actionLoading, setActionLoading] = useState('');
  const [actionMsg, setActionMsg] = useState('');

  const adminHeaders = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  function load() {
    setLoading(true);
    fetch(`${API}/admin/subscribers/${userId}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(d => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  }

  useEffect(() => { load(); }, [userId, token]); // eslint-disable-line react-hooks/exhaustive-deps

  async function callAction(key, url, method, body) {
    setActionLoading(key);
    setActionMsg('');
    try {
      const res = await fetch(url, { method, headers: adminHeaders, body: body ? JSON.stringify(body) : undefined });
      const json = await res.json();
      if (!res.ok) {
        setActionMsg(`Error: ${json.detail || 'Unknown error'}`);
      } else {
        setActionMsg('Done.');
        load();
        if (onUserUpdated) onUserUpdated();
      }
    } catch {
      setActionMsg('Network error.');
    } finally {
      setActionLoading('');
    }
  }

  async function handleDelete() {
    setActionLoading('delete');
    setActionMsg('');
    try {
      const res = await fetch(`${API}/admin/subscribers/${userId}?reason=${encodeURIComponent(deleteReason)}`, {
        method: 'DELETE',
        headers: adminHeaders,
      });
      const json = await res.json();
      if (!res.ok) {
        setActionMsg(`Error: ${json.detail || 'Unknown error'}`);
        setActionLoading('');
      } else {
        // User no longer exists — refresh the list and close this panel rather than reloading it.
        if (onUserUpdated) onUserUpdated();
        onClose();
      }
    } catch {
      setActionMsg('Network error.');
      setActionLoading('');
    }
  }

  const isSuspended = data?.user?.is_suspended;
  const freeUntil = data?.user?.free_access_until;
  const freeAccessActive = freeUntil && new Date(freeUntil) > new Date();

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="relative z-10 w-full max-w-xl bg-white shadow-2xl overflow-y-auto flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-stone-200 bg-[#1e3a8a] sticky top-0 z-10">
          <h2 className="text-lg font-semibold text-white font-['Libre_Baskerville']">User Detail</h2>
          <button onClick={onClose} className="text-white/70 hover:text-white transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        {loading ? (
          <div className="flex-1 flex items-center justify-center py-24">
            <div className="w-8 h-8 border-2 border-[#1e3a8a] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : !data ? (
          <div className="flex-1 flex items-center justify-center text-stone-500 py-24">Failed to load user.</div>
        ) : (
          <div className="p-6 space-y-7">

            {/* ── Section 1: User Overview ── */}
            <div>
              <SectionHeader>User Overview</SectionHeader>
              <div className="flex items-start gap-4 mb-4">
                {data.user.picture ? (
                  <img src={data.user.picture} alt="" className="w-14 h-14 rounded-full object-cover flex-shrink-0" />
                ) : (
                  <div className="w-14 h-14 rounded-full bg-[#1e3a8a]/10 flex items-center justify-center text-[#1e3a8a] font-bold text-xl flex-shrink-0">
                    {data.user.name?.[0]?.toUpperCase() || '?'}
                  </div>
                )}
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-semibold text-[#1f2937] text-lg leading-tight">{data.user.name}</p>
                    {isSuspended && (
                      <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700 flex items-center gap-1">
                        <ShieldOff className="w-3 h-3" /> Suspended
                      </span>
                    )}
                    {freeAccessActive && (
                      <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700 flex items-center gap-1">
                        <ShieldCheck className="w-3 h-3" /> Free access
                      </span>
                    )}
                  </div>
                  <p className="text-stone-500 text-sm">{data.user.email}</p>
                  <p className="text-stone-400 text-xs mt-0.5 font-mono">{data.user.user_id}</p>
                </div>
              </div>

              <div className="bg-stone-50 rounded-xl p-4 space-y-0.5">
                <InfoRow label="Joined">{formatDate(data.user.created_at)}</InfoRow>
                <InfoRow label="Last login">{formatDateTime(data.last_login) || '—'}</InfoRow>
                <InfoRow label="Signup #">{data.user.signup_number || '—'}</InfoRow>
                {data.user.is_early_adopter && (
                  <InfoRow label="Early adopter"><span className="text-green-600 font-medium">Yes</span></InfoRow>
                )}
                <InfoRow label="Subscription">
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${TIER_COLORS[data.user.subscription_tier] || 'bg-gray-100 text-gray-600'}`}>
                    {TIER_LABELS[data.user.subscription_tier] || data.user.subscription_tier || 'Free'}
                  </span>
                </InfoRow>
                {data.user.subscription_status && (
                  <InfoRow label="Sub status">
                    <span className={`text-xs px-2 py-0.5 rounded-full ${data.user.subscription_status === 'active' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'}`}>
                      {data.user.subscription_status}
                    </span>
                  </InfoRow>
                )}
                {freeUntil && (
                  <InfoRow label="Free access until">
                    <span className={freeAccessActive ? 'text-emerald-700 font-medium' : 'text-stone-400 line-through'}>
                      {formatDate(freeUntil)}
                    </span>
                  </InfoRow>
                )}
                {data.user.stripe_customer_id && (
                  <InfoRow label="Stripe customer">
                    <span className="flex items-center gap-1">
                      <span className="font-mono text-xs text-stone-600">{data.user.stripe_customer_id}</span>
                      <CopyButton text={data.user.stripe_customer_id} />
                      <a
                        href={`https://dashboard.stripe.com/customers/${data.user.stripe_customer_id}`}
                        target="_blank"
                        rel="noreferrer"
                        className="text-stone-400 hover:text-[#1e3a8a] transition-colors"
                        title="Open in Stripe"
                      >
                        <ExternalLink className="w-3.5 h-3.5" />
                      </a>
                    </span>
                  </InfoRow>
                )}
              </div>
            </div>

            {/* ── Section 2: Usage Statistics ── */}
            <div>
              <SectionHeader>Usage Statistics</SectionHeader>
              <div className="bg-stone-50 rounded-xl p-4 space-y-0.5 mb-3">
                <InfoRow label="Analyses this month">{data.user.monthly_analysis_count ?? 0}</InfoRow>
                <InfoRow label="Total analyses">{data.analysis_count}</InfoRow>
              </div>

              {/* Feature usage */}
              {(Object.keys(data.feature_usage_this_month || {}).length > 0 || Object.keys(data.feature_usage_alltime || {}).length > 0) && (
                <div className="bg-stone-50 rounded-xl overflow-hidden">
                  <div className="grid grid-cols-3 px-4 py-2 border-b border-stone-100 text-xs font-semibold text-stone-400 uppercase tracking-wider">
                    <span>Feature</span>
                    <span className="text-center">This month</span>
                    <span className="text-center">All time</span>
                  </div>
                  {Array.from(new Set([
                    ...Object.keys(data.feature_usage_this_month || {}),
                    ...Object.keys(data.feature_usage_alltime || {}),
                  ])).map(feat => (
                    <div key={feat} className="grid grid-cols-3 px-4 py-2 border-b border-stone-50 last:border-0 text-sm">
                      <span className="text-stone-600 capitalize">{feat.replace(/_/g, ' ')}</span>
                      <span className="text-center text-[#1f2937] font-medium">{(data.feature_usage_this_month || {})[feat] ?? 0}</span>
                      <span className="text-center text-stone-500">{(data.feature_usage_alltime || {})[feat] ?? 0}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Feature access flags */}
              <div className="mt-3 flex flex-wrap gap-2">
                {Object.entries(data.feature_access).map(([feat, val]) => (
                  <span key={feat} className={`flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${val ? 'bg-green-50 text-green-700' : 'bg-stone-100 text-stone-400'}`}>
                    <FeatureIcon value={val} />
                    {feat.replace(/_/g, ' ')}
                  </span>
                ))}
              </div>
            </div>

            {/* ── Section 3: Manual Subscription Controls ── */}
            <div>
              <SectionHeader>Subscription Controls</SectionHeader>
              {actionMsg && (
                <p className={`text-xs mb-3 px-3 py-2 rounded-lg ${actionMsg.startsWith('Error') ? 'bg-red-50 text-red-600' : 'bg-green-50 text-green-700'}`}>
                  {actionMsg}
                </p>
              )}

              <div className="space-y-4">
                {/* Grant free access */}
                <div className="border border-stone-200 rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <Calendar className="w-4 h-4 text-[#1e3a8a]" />
                    <p className="text-sm font-semibold text-[#1f2937]">Grant Free Access</p>
                  </div>
                  <div className="flex gap-2 mb-2">
                    <div className="flex-1">
                      <label className="text-xs text-stone-500 block mb-1">Days</label>
                      <Input
                        type="number"
                        min="1"
                        max="365"
                        value={extensionDays}
                        onChange={e => setExtensionDays(e.target.value)}
                        className="h-8 text-sm"
                      />
                    </div>
                    <div className="flex-[2]">
                      <label className="text-xs text-stone-500 block mb-1">Reason (optional)</label>
                      <Input
                        value={extensionReason}
                        onChange={e => setExtensionReason(e.target.value)}
                        placeholder="e.g. support credit"
                        className="h-8 text-sm"
                      />
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      className="bg-[#1e3a8a] hover:bg-[#1e3a8a]/90 text-white h-8 text-xs"
                      disabled={actionLoading === 'grant'}
                      onClick={() => callAction('grant', `${API}/admin/subscribers/${userId}/grant-extension`, 'POST', { days: parseInt(extensionDays), reason: extensionReason })}
                    >
                      {actionLoading === 'grant' ? 'Saving…' : 'Grant'}
                    </Button>
                    {freeUntil && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-8 text-xs text-red-600 border-red-200 hover:bg-red-50"
                        disabled={actionLoading === 'remove-ext'}
                        onClick={() => callAction('remove-ext', `${API}/admin/subscribers/${userId}/grant-extension`, 'DELETE')}
                      >
                        {actionLoading === 'remove-ext' ? 'Removing…' : 'Remove extension'}
                      </Button>
                    )}
                  </div>
                </div>

                {/* Override tier */}
                <div className="border border-stone-200 rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <Settings2 className="w-4 h-4 text-[#1e3a8a]" />
                    <p className="text-sm font-semibold text-[#1f2937]">Override Subscription Tier</p>
                  </div>
                  <div className="flex gap-2 mb-2">
                    <select
                      value={overrideTier}
                      onChange={e => setOverrideTier(e.target.value)}
                      className="flex-1 text-sm border border-stone-200 rounded-md px-3 py-1.5 bg-white text-stone-700 focus:outline-none focus:ring-2 focus:ring-[#1e3a8a]/30"
                    >
                      <option value="">— select tier —</option>
                      {ALL_TIERS.map(t => (
                        <option key={t} value={t}>{TIER_LABELS[t] || t}</option>
                      ))}
                    </select>
                    <Input
                      value={overrideReason}
                      onChange={e => setOverrideReason(e.target.value)}
                      placeholder="Reason"
                      className="flex-[2] h-9 text-sm"
                    />
                  </div>
                  <Button
                    size="sm"
                    className="bg-[#1e3a8a] hover:bg-[#1e3a8a]/90 text-white h-8 text-xs"
                    disabled={!overrideTier || actionLoading === 'tier'}
                    onClick={() => callAction('tier', `${API}/admin/subscribers/${userId}/override-tier`, 'POST', { tier: overrideTier, reason: overrideReason })}
                  >
                    {actionLoading === 'tier' ? 'Saving…' : 'Apply'}
                  </Button>
                </div>

                {/* Reset usage */}
                <div className="border border-stone-200 rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <RotateCcw className="w-4 h-4 text-amber-600" />
                    <p className="text-sm font-semibold text-[#1f2937]">Reset Monthly Usage</p>
                  </div>
                  <p className="text-xs text-stone-500 mb-3">Clears analysis count and all feature usage for the current month.</p>
                  <div className="flex gap-2">
                    <Input
                      value={resetReason}
                      onChange={e => setResetReason(e.target.value)}
                      placeholder="Reason (optional)"
                      className="h-8 text-sm flex-1"
                    />
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-8 text-xs text-amber-700 border-amber-200 hover:bg-amber-50"
                      disabled={actionLoading === 'reset'}
                      onClick={() => callAction('reset', `${API}/admin/subscribers/${userId}/reset-usage`, 'POST', { reason: resetReason })}
                    >
                      {actionLoading === 'reset' ? 'Resetting…' : 'Reset'}
                    </Button>
                  </div>
                </div>

                {/* Suspend / Unsuspend */}
                <div className="border border-stone-200 rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-3">
                    {isSuspended ? <ShieldCheck className="w-4 h-4 text-green-600" /> : <AlertTriangle className="w-4 h-4 text-red-500" />}
                    <p className="text-sm font-semibold text-[#1f2937]">
                      {isSuspended ? 'Unsuspend Account' : 'Suspend Account'}
                    </p>
                  </div>
                  {!isSuspended && (
                    <p className="text-xs text-stone-500 mb-3">Suspended users cannot log in or use any features.</p>
                  )}
                  <div className="flex gap-2">
                    <Input
                      value={suspendReason}
                      onChange={e => setSuspendReason(e.target.value)}
                      placeholder={isSuspended ? 'Reason to unsuspend (optional)' : 'Reason for suspension'}
                      className="h-8 text-sm flex-1"
                    />
                    {isSuspended ? (
                      <Button
                        size="sm"
                        className="h-8 text-xs bg-green-600 hover:bg-green-700 text-white"
                        disabled={actionLoading === 'unsuspend'}
                        onClick={() => callAction('unsuspend', `${API}/admin/subscribers/${userId}/unsuspend`, 'POST', { reason: suspendReason })}
                      >
                        {actionLoading === 'unsuspend' ? 'Saving…' : 'Unsuspend'}
                      </Button>
                    ) : (
                      <Button
                        size="sm"
                        className="h-8 text-xs bg-red-600 hover:bg-red-700 text-white"
                        disabled={actionLoading === 'suspend'}
                        onClick={() => callAction('suspend', `${API}/admin/subscribers/${userId}/suspend`, 'POST', { reason: suspendReason })}
                      >
                        {actionLoading === 'suspend' ? 'Saving…' : 'Suspend'}
                      </Button>
                    )}
                  </div>
                </div>

                {/* Cancel subscription */}
                {data.user.subscription_tier && data.user.subscription_tier !== 'free' && (
                  <div className="border border-stone-200 rounded-xl p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <XCircle className="w-4 h-4 text-red-500" />
                      <p className="text-sm font-semibold text-[#1f2937]">Cancel Subscription</p>
                    </div>
                    <p className="text-xs text-stone-500 mb-3">
                      {data.user.stripe_subscription_id
                        ? 'Cancels their Stripe subscription immediately (no refund) and reverts them to the free tier.'
                        : 'No Stripe subscription on file — this will just revert their tier to free locally.'}
                    </p>
                    <div className="flex gap-2">
                      <Input
                        value={cancelReason}
                        onChange={e => setCancelReason(e.target.value)}
                        placeholder="Reason (optional)"
                        className="h-8 text-sm flex-1"
                      />
                      <Button
                        size="sm"
                        className="h-8 text-xs bg-red-600 hover:bg-red-700 text-white"
                        disabled={actionLoading === 'cancel'}
                        onClick={() => callAction('cancel', `${API}/admin/subscribers/${userId}/cancel-subscription`, 'POST', { reason: cancelReason })}
                      >
                        {actionLoading === 'cancel' ? 'Cancelling…' : 'Cancel Subscription'}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* ── Section 4: Admin Activity Log ── */}
            <div>
              <SectionHeader>Admin Activity Log</SectionHeader>
              {data.admin_actions && data.admin_actions.length > 0 ? (
                <div className="space-y-2">
                  {data.admin_actions.map(a => (
                    <div key={a.action_id} className="border border-stone-100 rounded-lg p-3">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex items-center gap-2">
                          <Clock className="w-3.5 h-3.5 text-stone-300 flex-shrink-0 mt-0.5" />
                          <span className="text-sm font-medium text-[#1f2937] capitalize">{a.action_type.replace(/_/g, ' ')}</span>
                        </div>
                        <span className="text-xs text-stone-400 flex-shrink-0">{formatDateTime(a.created_at)}</span>
                      </div>
                      {(a.previous_value || a.new_value) && (
                        <p className="text-xs text-stone-500 mt-1 ml-5.5">
                          {a.previous_value && <span className="line-through text-stone-400 mr-1">{a.previous_value}</span>}
                          {a.new_value && <span className="text-[#1e3a8a] font-medium">→ {a.new_value}</span>}
                        </p>
                      )}
                      {a.reason && (
                        <p className="text-xs text-stone-400 mt-0.5 ml-5.5 italic">"{a.reason}"</p>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-stone-400 py-4 text-center">No admin actions recorded for this user.</p>
              )}
            </div>

            {/* ── Recent Analyses ── */}
            {data.recent_analyses.length > 0 && (
              <div>
                <SectionHeader>Recent Analyses</SectionHeader>
                <div className="space-y-2">
                  {data.recent_analyses.map(a => (
                    <div key={a.analysis_id} className="text-sm border border-stone-100 rounded-lg p-3">
                      <p className="font-medium text-[#1f2937] truncate">{a.video_title || 'Untitled'}</p>
                      <p className="text-stone-400 text-xs mt-0.5">{formatDate(a.created_at)}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* ── Transactions ── */}
            {data.transactions.length > 0 && (
              <div>
                <SectionHeader>Payment Transactions</SectionHeader>
                <div className="space-y-2">
                  {data.transactions.map((t, i) => (
                    <div key={i} className="text-sm border border-stone-100 rounded-lg p-3">
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-[#1f2937]">{t.plan || t.plan_id || '—'}</span>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${t.status === 'completed' ? 'bg-green-100 text-green-700' : 'bg-stone-100 text-stone-600'}`}>
                          {t.status || '—'}
                        </span>
                      </div>
                      <p className="text-stone-400 text-xs mt-0.5">{formatDate(t.created_at)}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* ── Danger Zone: Delete User ── */}
            <div>
              <SectionHeader>Danger Zone</SectionHeader>
              <div className="border-2 border-red-200 bg-red-50/50 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Trash2 className="w-4 h-4 text-red-600" />
                  <p className="text-sm font-semibold text-red-700">Delete User Permanently</p>
                </div>
                <p className="text-xs text-red-600/80 mb-3">
                  Cancels any active Stripe subscription, then permanently deletes this user and all their
                  data (sessions, usage history, sermon analyses, payment records). This cannot be undone.
                </p>
                <div className="space-y-2">
                  <Input
                    value={deleteReason}
                    onChange={e => setDeleteReason(e.target.value)}
                    placeholder="Reason (optional)"
                    className="h-8 text-sm bg-white"
                  />
                  <div className="flex gap-2">
                    <Input
                      value={deleteConfirmText}
                      onChange={e => setDeleteConfirmText(e.target.value)}
                      placeholder={`Type "${data.user.email}" to confirm`}
                      className="h-8 text-sm flex-1 bg-white"
                    />
                    <Button
                      size="sm"
                      className="h-8 text-xs bg-red-600 hover:bg-red-700 text-white disabled:opacity-40"
                      disabled={deleteConfirmText !== data.user.email || actionLoading === 'delete'}
                      onClick={handleDelete}
                    >
                      {actionLoading === 'delete' ? 'Deleting…' : 'Delete Permanently'}
                    </Button>
                  </div>
                </div>
              </div>
            </div>

          </div>
        )}
      </div>
    </div>
  );
}

// ── Activity Tab ──────────────────────────────────────────────────
function ActivityTab({ token }) {
  const [activity, setActivity] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/admin/activity`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) setActivity(await res.json());
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="w-8 h-8 border-2 border-[#1e3a8a] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!activity) {
    return <div className="text-center py-24 text-stone-400">Failed to load activity data.</div>;
  }

  return (
    <div className="space-y-6">
      {/* Summary stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard icon={LogIn} label="Logins (30d)" value={activity.logins_30d} color="text-[#1e3a8a]" />
        <StatCard icon={Users} label="New Signups (30d)" value={activity.signups_30d} color="text-green-700" />
        <StatCard icon={BarChart3} label="Analyses (30d)" value={activity.analyses_30d} color="text-violet-600" />
        <StatCard icon={CreditCard} label="Paid Conversions (30d)" value={activity.paid_30d} color="text-amber-600" />
      </div>

      {/* Charts grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-white rounded-xl border border-stone-200 p-5">
          <p className="text-sm font-semibold text-stone-600 mb-3">Logins — last 30 days</p>
          <MiniChart data={activity.logins_by_day} color="#1e3a8a" label="Logins" />
        </div>
        <div className="bg-white rounded-xl border border-stone-200 p-5">
          <p className="text-sm font-semibold text-stone-600 mb-3">New Signups — last 30 days</p>
          <MiniChart data={activity.signups_by_day} color="#16a34a" label="Signups" />
        </div>
        <div className="bg-white rounded-xl border border-stone-200 p-5">
          <p className="text-sm font-semibold text-stone-600 mb-3">Analyses Run — last 30 days</p>
          <MiniChart data={activity.analyses_by_day} color="#7c3aed" label="Analyses" />
        </div>
        <div className="bg-white rounded-xl border border-stone-200 p-5">
          <p className="text-sm font-semibold text-stone-600 mb-3">Paid Conversions — last 30 days</p>
          <MiniChart data={activity.paid_by_day} color="#d97706" label="Conversions" />
        </div>
      </div>

      {/* Recent logins */}
      <div className="bg-white rounded-xl border border-stone-200">
        <div className="px-5 py-4 border-b border-stone-100">
          <p className="text-sm font-semibold text-stone-600">Recent Logins</p>
        </div>
        <div className="divide-y divide-stone-50">
          {activity.recent_logins.length === 0 ? (
            <p className="text-center text-stone-400 py-8 text-sm">No login events recorded yet.</p>
          ) : activity.recent_logins.map((event, i) => (
            <div key={i} className="flex items-center gap-3 px-5 py-3">
              {event.user?.picture ? (
                <img src={event.user.picture} alt="" className="w-8 h-8 rounded-full object-cover flex-shrink-0" />
              ) : (
                <div className="w-8 h-8 rounded-full bg-[#1e3a8a]/10 flex-shrink-0 flex items-center justify-center text-[#1e3a8a] text-xs font-bold">
                  {event.user?.name?.[0]?.toUpperCase() || '?'}
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-[#1f2937] truncate">{event.user?.name || event.user_id}</p>
                <p className="text-xs text-stone-400 truncate">{event.user?.email}</p>
              </div>
              <div className="text-right flex-shrink-0">
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${event.method === 'google' ? 'bg-blue-50 text-blue-600' : 'bg-stone-100 text-stone-600'}`}>
                  {event.method}
                </span>
                <p className="text-xs text-stone-400 mt-0.5">{formatDateTime(event.created_at)}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Main Admin Page ───────────────────────────────────────────────
export default function AdminPage() {
  const [token, setToken] = useState(() => sessionStorage.getItem('admin_token') || '');
  const [tokenInput, setTokenInput] = useState('');
  const [authError, setAuthError] = useState('');
  const [activeTab, setActiveTab] = useState('overview');

  const [stats, setStats] = useState(null);
  const [subscribers, setSubscribers] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [tierFilter, setTierFilter] = useState('');
  const [loading, setLoading] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);

  const LIMIT = 50;

  const authHeaders = useCallback(() => ({
    Authorization: `Bearer ${token}`,
  }), [token]);

  const loadStats = useCallback(async () => {
    const res = await fetch(`${API}/admin/stats`, { headers: authHeaders() });
    if (!res.ok) return null;
    return res.json();
  }, [authHeaders]);

  const loadSubscribers = useCallback(async (pg = 1, srch = '', tier = '') => {
    setLoading(true);
    const params = new URLSearchParams({ page: pg, limit: LIMIT });
    if (srch) params.set('search', srch);
    if (tier) params.set('tier', tier);
    const res = await fetch(`${API}/admin/subscribers?${params}`, { headers: authHeaders() });
    setLoading(false);
    if (!res.ok) return;
    const data = await res.json();
    setSubscribers(data.users);
    setTotal(data.total);
  }, [authHeaders]);

  useEffect(() => {
    if (!token) return;
    loadStats().then(s => { if (s) setStats(s); });
    loadSubscribers(page, search, tierFilter);
  }, [token, page, search, tierFilter, loadStats, loadSubscribers]);

  function handleLogin(e) {
    e.preventDefault();
    setAuthError('');
    fetch(`${API}/admin/stats`, {
      headers: { Authorization: `Bearer ${tokenInput}` },
    }).then(r => {
      if (r.ok) {
        sessionStorage.setItem('admin_token', tokenInput);
        setToken(tokenInput);
      } else {
        setAuthError('Invalid admin token.');
      }
    }).catch(() => setAuthError('Could not connect to backend.'));
  }

  function handleLogout() {
    sessionStorage.removeItem('admin_token');
    setToken('');
    setTokenInput('');
    setStats(null);
    setSubscribers([]);
  }

  function handleSearch(e) {
    e.preventDefault();
    setSearch(searchInput);
    setPage(1);
  }

  // ── Auth screen ──
  if (!token) {
    return (
      <div className="min-h-screen bg-[#fafaf9] flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-lg border border-stone-200 p-8 w-full max-w-sm">
          <div className="flex items-center gap-2 mb-6">
            <Crown className="w-6 h-6 text-[#1e3a8a]" />
            <h1 className="text-xl font-bold text-[#1e3a8a] font-['Libre_Baskerville']">Admin Panel</h1>
          </div>
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="text-sm font-medium text-stone-700 block mb-1">Admin Token</label>
              <Input
                type="password"
                value={tokenInput}
                onChange={e => setTokenInput(e.target.value)}
                placeholder="Enter ADMIN_SECRET"
                autoFocus
              />
            </div>
            {authError && <p className="text-sm text-red-600">{authError}</p>}
            <Button type="submit" className="w-full bg-[#1e3a8a] hover:bg-[#1e3a8a]/90 text-white">
              Access Dashboard
            </Button>
          </form>
        </div>
      </div>
    );
  }

  // ── Dashboard ──
  const totalPages = Math.ceil(total / LIMIT);

  const tierOptions = [
    { value: '', label: 'All tiers' },
    ...Object.entries(TIER_LABELS).map(([v, l]) => ({ value: v, label: l })),
  ];

  return (
    <div className="min-h-screen bg-[#fafaf9]">
      {/* Header */}
      <div className="bg-[#1e3a8a] text-white px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Crown className="w-5 h-5" />
          <h1 className="text-lg font-bold font-['Libre_Baskerville']">SermonSift Admin</h1>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => {
              loadStats().then(s => { if (s) setStats(s); });
              loadSubscribers(page, search, tierFilter);
            }}
            className="text-white/70 hover:text-white transition-colors"
            title="Refresh"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
          <button
            onClick={handleLogout}
            className="flex items-center gap-1.5 text-sm text-white/70 hover:text-white transition-colors"
          >
            <LogOut className="w-4 h-4" />
            Sign out
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white border-b border-stone-200 px-6">
        <div className="flex gap-1 max-w-7xl mx-auto">
          {[
            { id: 'overview', label: 'Overview', icon: Users },
            { id: 'activity', label: 'Activity', icon: Activity },
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-1.5 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-[#1e3a8a] text-[#1e3a8a]'
                  : 'border-transparent text-stone-500 hover:text-stone-700'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6 space-y-6">
        {activeTab === 'activity' ? (
          <ActivityTab token={token} />
        ) : (
          <>
            {/* Stats */}
            {stats && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <StatCard icon={Users} label="Total Users" value={stats.total_users} sub={`${stats.recent_signups_30d} joined last 30d`} />
                <StatCard icon={Crown} label="Paid Subscribers" value={stats.paid_users} sub={`${stats.active_subscriptions} active`} color="text-amber-600" />
                <StatCard icon={BarChart3} label="Free Users" value={stats.free_users} color="text-stone-500" />
                <StatCard icon={TrendingUp} label="Total Analyses" value={stats.total_analyses} color="text-green-700" />
              </div>
            )}

            {/* Tier breakdown */}
            {stats?.tier_breakdown && (
              <div className="bg-white rounded-xl border border-stone-200 p-5">
                <p className="text-sm font-semibold text-stone-500 uppercase tracking-wider mb-3">Tier Breakdown</p>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(stats.tier_breakdown)
                    .sort((a, b) => b[1] - a[1])
                    .map(([tier, count]) => (
                      <button
                        key={tier}
                        onClick={() => { setTierFilter(tier === tierFilter ? '' : tier); setPage(1); }}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium border transition-all ${
                          tierFilter === tier
                            ? 'border-[#1e3a8a] bg-[#1e3a8a] text-white'
                            : `${TIER_COLORS[tier] || 'bg-gray-100 text-gray-600'} border-transparent hover:border-stone-300`
                        }`}
                      >
                        {TIER_LABELS[tier] || tier}
                        <span className={`text-xs font-bold ${tierFilter === tier ? 'text-white/80' : ''}`}>{count}</span>
                      </button>
                    ))}
                </div>
              </div>
            )}

            {/* Subscribers table */}
            <div className="bg-white rounded-xl border border-stone-200">
              {/* Table toolbar */}
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 p-4 border-b border-stone-100">
                <form onSubmit={handleSearch} className="flex gap-2 flex-1">
                  <div className="relative flex-1 max-w-xs">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                    <Input
                      value={searchInput}
                      onChange={e => setSearchInput(e.target.value)}
                      placeholder="Search name or email…"
                      className="pl-9"
                    />
                  </div>
                  <Button type="submit" variant="outline" size="sm">Search</Button>
                  {(search || tierFilter) && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => { setSearch(''); setSearchInput(''); setTierFilter(''); setPage(1); }}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  )}
                </form>

                <select
                  value={tierFilter}
                  onChange={e => { setTierFilter(e.target.value); setPage(1); }}
                  className="text-sm border border-stone-200 rounded-md px-3 py-2 bg-white text-stone-700 focus:outline-none focus:ring-2 focus:ring-[#1e3a8a]/30"
                >
                  {tierOptions.map(o => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>

                <span className="text-sm text-stone-400 whitespace-nowrap">{total} user{total !== 1 ? 's' : ''}</span>
              </div>

              {/* Table */}
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-stone-100 bg-stone-50">
                      <th className="text-left px-4 py-3 font-semibold text-stone-500 text-xs uppercase tracking-wider">User</th>
                      <th className="text-left px-4 py-3 font-semibold text-stone-500 text-xs uppercase tracking-wider">Tier</th>
                      <th className="text-left px-4 py-3 font-semibold text-stone-500 text-xs uppercase tracking-wider">Status</th>
                      <th className="text-left px-4 py-3 font-semibold text-stone-500 text-xs uppercase tracking-wider hidden md:table-cell">Usage</th>
                      <th className="text-left px-4 py-3 font-semibold text-stone-500 text-xs uppercase tracking-wider hidden lg:table-cell">Joined</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-stone-50">
                    {loading ? (
                      <tr>
                        <td colSpan={5} className="text-center py-12 text-stone-400">
                          <div className="w-6 h-6 border-2 border-[#1e3a8a] border-t-transparent rounded-full animate-spin mx-auto" />
                        </td>
                      </tr>
                    ) : subscribers.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="text-center py-12 text-stone-400">No users found.</td>
                      </tr>
                    ) : (
                      subscribers.map(u => (
                        <tr
                          key={u.user_id}
                          onClick={() => setSelectedUserId(u.user_id)}
                          className="hover:bg-stone-50 cursor-pointer transition-colors"
                        >
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-3">
                              {u.picture ? (
                                <img src={u.picture} alt="" className="w-8 h-8 rounded-full object-cover flex-shrink-0" />
                              ) : (
                                <div className="w-8 h-8 rounded-full bg-[#1e3a8a]/10 flex-shrink-0 flex items-center justify-center text-[#1e3a8a] text-xs font-bold">
                                  {u.name?.[0]?.toUpperCase() || '?'}
                                </div>
                              )}
                              <div className="min-w-0">
                                <p className="font-medium text-[#1f2937] truncate">{u.name}</p>
                                <p className="text-stone-400 text-xs truncate">{u.email}</p>
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${TIER_COLORS[u.subscription_tier] || 'bg-gray-100 text-gray-600'}`}>
                              {TIER_LABELS[u.subscription_tier] || u.subscription_tier || 'Free'}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            {u.subscription_status ? (
                              <span className={`text-xs px-2 py-0.5 rounded-full ${
                                u.subscription_status === 'active' ? 'bg-green-100 text-green-700' :
                                u.subscription_status === 'canceled' || u.subscription_status === 'cancelled' ? 'bg-red-100 text-red-600' :
                                'bg-stone-100 text-stone-600'
                              }`}>
                                {u.subscription_status}
                              </span>
                            ) : (
                              <span className="text-stone-300 text-xs">—</span>
                            )}
                          </td>
                          <td className="px-4 py-3 hidden md:table-cell">
                            <span className="text-stone-600">{u.monthly_analysis_count ?? 0}</span>
                            <span className="text-stone-400"> / mo</span>
                            {PAID_TIERS.includes(u.subscription_tier) ? (
                              <span className="text-stone-400 text-xs ml-1">(unlimited)</span>
                            ) : (
                              <span className="text-stone-400 text-xs ml-1">(3 free)</span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-stone-500 hidden lg:table-cell">{formatDate(u.created_at)}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between px-4 py-3 border-t border-stone-100">
                  <span className="text-sm text-stone-500">
                    Page {page} of {totalPages}
                  </span>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={page <= 1}
                      onClick={() => setPage(p => p - 1)}
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={page >= totalPages}
                      onClick={() => setPage(p => p + 1)}
                    >
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* User detail panel */}
      {selectedUserId && (
        <UserDetailPanel
          userId={selectedUserId}
          token={token}
          onClose={() => setSelectedUserId(null)}
          onUserUpdated={() => loadSubscribers(page, search, tierFilter)}
        />
      )}
    </div>
  );
}
